import { useEffect, useMemo, useState, useRef } from "react";
import { CheckPicker } from "rsuite/esm";
import { Row, Col, Alert, Badge, Input } from "reactstrap";
import { useAtom } from "jotai";
import produce from "immer";

import MatchRecordsConfig from "../MatchRecordsConfig";
import MappedHeader from "../MappedHeader";
import RelateFieldsHeader from "../RelateFieldsHeader";
import CardCollapse from "../CardCollapse";
import ComplexMappedHeader from "../ComplexMappedHeader";
import ExcelCSVInput from "components/Inputs/ExcelCSVInput";
import RequiredTitle from "elements/RequiredTitle";
import useModule from "hooks/useModule";
import useFieldMap from "hooks/useFieldMap";
import { callFunctionIfExists } from "utils/GeneralUtils";
import useErrorMessage from "hooks/useErrorMessage";
import FieldUtils from "utils/FieldUtils";
import {
  handleComplexAutoMap,
  handleMultiSubitemAutoMap,
  handleSubitemAutoMap,
} from "../ImportDataWidgetUtils";
import { getBaseFields } from "../ImportDataWidget";

const fieldsToSkip = ["_RecordName"];

const RecordCreateUpdateMenu = ({
  moduleAPIName,
  onChange,
  actions,
  existingData,
  externalHeaders,
  mappedHeadersAtom,
  compatibleActions,
  manageMapValue,
  manageItemValue,
  usesActions,
}) => {
  const { module } = useModule(moduleAPIName);
  const fieldMap = useFieldMap(moduleAPIName);
  const { error, setError } = useErrorMessage();

  const [fileHeaders, setFileHeaders] = useState([]);
  const [mappedHeaders, setMappedHeaders] = useAtom(mappedHeadersAtom);
  const [matchRecords, setMatchRecords] = useState("");
  const [selectedMatchFields, setSelectedMatchFields] = useState([]);
  const [matchAction, setMatchAction] = useState("");
  const generateRulesPayloadRef = useRef();
  const onChangeRef = useRef();
  generateRulesPayloadRef.current = generateRulesPayload;
  onChangeRef.current = onChange;

  // Sync external headers with local state if provided.
  useEffect(() => {
    if (!externalHeaders || !externalHeaders?.length) return;

    handleHeadersChange(externalHeaders);
    // eslint-disable-next-line
  }, [externalHeaders]);

  // We set mapped headers all over in children of this widget, so we need to react to those updates.
  // (Bad Practice but necessary to maintain performance boost design architecture)
  useEffect(() => {
    const payload = generateRulesPayloadRef.current({ headers: mappedHeaders });
    callFunctionIfExists(onChangeRef.current, payload);
  }, [mappedHeaders]);

  const selectedFields = Object.keys(mappedHeaders);

  const availableActions = useMemo(() => {
    if (!actions) return [];

    return actions.filter((a) => {
      if (existingData?.id === a.id) return false;

      return compatibleActions.includes(a.Type) && a.AvailableToFutureActions;
    });
  }, [actions, existingData?.id]);

  useEffect(() => {
    if (!existingData) return;
    const headers = {};

    for (const item of existingData.FieldMapping) {
      let value;

      if (item?.Source === "Complex") {
        const newFields = [];
        for (const value of item.Values) {
          const nestedFields = { Fields: {} };

          for (const nestedItem of value.Values) {
            let finalValue;

            if (nestedItem.Source === "Complex") {
              const newFields2 = [];

              for (const value2 of nestedItem.Values) {
                const nestedFields2 = { Fields: {} };

                for (const nestedItem2 of value2.Values) {
                  let finalValue2 = manageItemValue(nestedItem2);

                  nestedFields2.Fields[nestedItem2.Field] = finalValue2;
                }

                newFields2.push(nestedFields2);
              }

              finalValue = { Type: "Identifier", Fields: newFields2 };
            } else {
              finalValue = manageItemValue(nestedItem);
            }

            nestedFields.Fields[nestedItem.Field] = finalValue;
          }

          newFields.push(nestedFields);
        }

        value = { Type: "Identifier", Fields: newFields };
      } else {
        value = manageItemValue(item);
      }

      headers[item.Field] = value;
    }

    setMappedHeaders(headers);

    setMatchRecords(existingData?.Match ?? false);

    if (existingData?.Match) {
      setSelectedMatchFields(existingData?.MatchFields ?? []);
      setMatchAction(existingData?.MatchMethod ?? "update");
    }
  }, []);

  return (
    <>
      {!externalHeaders && (
        <>
          <CardCollapse
            title={
              <div className="d-flex align-items-center">
                Select A Sample File <Badge className="ml-2">Optional</Badge>
              </div>
            }
            description="Select a sample file to automatically map Data Identifiers for the import"
          >
            <ExcelCSVInput
              onHeadersChange={handleHeadersChange}
              onError={setError}
              onClear={resetState}
            />
          </CardCollapse>
          {error && (
            <Alert color="warning" className="mt-2 p-2">
              {error}
            </Alert>
          )}
        </>
      )}
      {module && (
        <>
          <hr></hr>
          <RelateFieldsHeader
            autoRelate={autoRelate}
            ignoreAll={ignoreAll}
            selectedFields={selectedFields}
            fileHeaders={fileHeaders}
          />
          <Row className="mb-2">
            <Col xs="6" sm="5">
              <h4 className="font-weight-600">Field</h4>
            </Col>
            <Col xs="6" sm="7">
              <h4 className="font-weight-600">Mapped To</h4>
            </Col>
          </Row>
          {selectedFields?.length ? (
            selectedFields.map((fieldAPIName, i) => {
              const field = fieldMap?.[fieldAPIName];
              const fieldType = field?.FieldType;
              const isComplex = FieldUtils.isComplex(fieldType);
              const isPartiallyComplex =
                FieldUtils.isPartiallyComplex(fieldType);
              const isSubitem = FieldUtils.fieldTypeIsSubitem(fieldType);
              const mappedHeader = mappedHeaders[field?.APIName];

              if (!field || !mappedHeader) return null;

              if (isComplex || isSubitem || isPartiallyComplex) {
                return (
                  <ComplexMappedHeader
                    key={i}
                    mappedHeader={mappedHeader}
                    field={field}
                    availableActions={availableActions}
                    subitemFieldMap={isSubitem ? fieldMap : null}
                    usesActions={usesActions}
                  />
                );
              }

              return (
                <Row
                  className="align-items-center border rounded mb-2 py-1"
                  style={{ marginLeft: 0, marginRight: 0 }}
                  key={i}
                >
                  <Col xs="6" sm="5">
                    {field?.Name}
                  </Col>
                  <Col xs="6" sm="7">
                    <MappedHeader
                      mappedHeader={mappedHeader}
                      field={field}
                      availableActions={availableActions}
                      usesActions={usesActions}
                    />
                  </Col>
                </Row>
              );
            })
          ) : (
            <div className="font-weight-500 border rounded bg-superlight px-2 py-1">
              No Column / Field pairs have been added.
            </div>
          )}
          <h5 className="mt-4">Add Field</h5>
          <CheckPicker
            data={module?.Fields ?? null}
            labelKey="Name"
            valueKey="APIName"
            placeholder={`Select ${module.Name || "Record"} Fields`}
            onChange={handleFieldsChange}
            value={selectedFields}
            cleanable={false}
            block
            preventOverflow
          />
          <hr></hr>
          <RequiredTitle
            title="Match Existing Records"
            size="md"
            titleCSS="mb-0"
          />
          <p>
            <small>Match data to existing records in {module.Name}</small>
          </p>
          <Input
            type="select"
            value={matchRecords}
            bsSize="sm"
            className="mb-4"
            onChange={updateMatchRecords}
          >
            <option disabled value="">
              Select an option
            </option>
            <option value={true}>Yes</option>
            <option value={false}>No</option>
          </Input>
          {matchRecords === true && (
            <MatchRecordsConfig
              mappedHeaders={mappedHeaders}
              fieldMap={fieldMap}
              matchAction={matchAction}
              onUpdateAction={handleMatchActionChange}
              matchFields={selectedMatchFields}
              onMatchFieldsChange={handleMatchFieldChange}
            />
          )}
        </>
      )}
    </>
  );

  function updateMatchRecords(e) {
    const value = e.target.value === "true" ? true : false;

    setMatchRecords(value);
    handleChange({ mRecords: value });
  }

  function generateRulesPayload({ headers, matchFields, mAction, mRecords }) {
    let finalHeaders = headers || mappedHeaders;
    let finalMatchFields = matchFields || selectedMatchFields;
    let finalMatchAction = mAction || matchAction;
    let finalMatchRecords = mRecords ?? matchRecords;

    const payload = {
      FieldMapping: [],
      Match: finalMatchRecords === false ? false : "",
      MatchFields: [],
      MatchMethod: "",
    };

    if (!Object.keys(finalHeaders).length) {
      return payload;
    }

    for (const [fieldName, mapValue] of Object.entries(finalHeaders)) {
      const Field = fieldName;

      const finalValue = { Field };

      if (mapValue?.Type === "Identifier") {
        finalValue.Source = "Complex";
        finalValue.Values = [];

        mapValue.Fields.forEach((f) => {
          const nestedValues = { Values: [] };

          Object.keys(f.Fields).forEach((field) => {
            const nestedFinalValue = { Field: field };

            // Handle subitem complex field
            if (f.Fields[field]?.Fields?.length) {
              nestedFinalValue.Source = "Complex";
              nestedFinalValue.Values = [];

              f.Fields[field].Fields.forEach((f2) => {
                const inner = { Values: [] };

                Object.keys(f2.Fields).forEach((field2) => {
                  const nestedFinalValue2 = { Field: field2 };

                  manageMapValue(f2.Fields[field2], nestedFinalValue2);

                  inner.Values.push(nestedFinalValue2);
                });

                nestedFinalValue.Values.push(inner);
              });
            } else {
              manageMapValue(f.Fields[field], nestedFinalValue);
            }

            nestedValues.Values.push(nestedFinalValue);
          });

          finalValue.Values.push(nestedValues);
        });
      } else {
        manageMapValue(mapValue, finalValue);
      }

      if (fieldName !== "") {
        payload.FieldMapping.push(finalValue);
      }
    }

    if (finalMatchRecords && finalMatchFields?.length && finalMatchAction) {
      payload.Match = true;
      payload.MatchFields = finalMatchFields;
      payload.MatchMethod = finalMatchAction;
    }

    return payload;
  }

  function resetState() {
    setFileHeaders([]);
    setMappedHeaders({});
    setMatchRecords("");
    setSelectedMatchFields([]);
    setMatchAction("");
  }

  function handleChange(props) {
    const payload = generateRulesPayload(props);
    callFunctionIfExists(onChange, payload);
  }

  function handleAddBlankField(fieldAPIName, headers) {
    const field = fieldMap[fieldAPIName];
    const fieldType = field?.FieldType;

    if (!fieldType) return;

    const isComplex = FieldUtils.isComplex(fieldType);
    const isSubitem = FieldUtils.fieldTypeIsSubitem(fieldType);

    if (isComplex || isSubitem) {
      headers[fieldAPIName] = {
        Type: "Identifier",
        Fields: [{ Fields: getBaseFields(fieldType, field?.SubitemFields) }],
      };
    } else {
      headers[fieldAPIName] = { Identifier: "" };
    }
  }

  function handleFieldsChange(_, e) {
    const newField = e.target.value;
    if (!newField) return;

    const nextHeaders = produce(mappedHeaders, (newHeaders) => {
      if (newHeaders.hasOwnProperty(newField)) {
        delete newHeaders[newField];
      } else {
        handleAddBlankField(newField, newHeaders);
      }
    });

    setMappedHeaders(nextHeaders);
  }

  // initializeMappedHeaders - Attempt to map fields to file headers
  function initializeMappedHeaders(headers, exists = false) {
    const lowercaseHeaders = headers.map((x) => x.toLowerCase());
    const newMappedHeaders = exists ? { ...mappedHeaders } : {};

    for (const field of module.Fields) {
      const fieldAPIName = field.APIName;
      const fieldType = field?.FieldType;
      let lowerAPIName = fieldAPIName.toLowerCase();
      const lowerName = field.Name.toLowerCase();
      const isComplex = FieldUtils.isComplex(fieldType);
      const isPartiallyComplex = FieldUtils.isPartiallyComplex(fieldType);
      const isSingleSubitem = fieldType === "Subitem";
      const isMultiSubitem = fieldType === "MultiSubitem";
      const isSingleRef = fieldType === "SingleReference";

      if (isComplex) {
        const complexHeaders = lowercaseHeaders.filter((header) =>
          header.startsWith(`${lowerAPIName}_`)
        );

        if (complexHeaders?.length > 0) {
          handleComplexAutoMap(
            newMappedHeaders,
            fieldAPIName,
            complexHeaders,
            lowercaseHeaders,
            headers
          );
        }
      } else if (isSingleSubitem || isPartiallyComplex) {
        const subitemHeaders = lowercaseHeaders.filter((header) =>
          header.startsWith(`${lowerAPIName}.`)
        );

        if (subitemHeaders?.length > 0) {
          handleSubitemAutoMap(
            newMappedHeaders,
            fieldAPIName,
            subitemHeaders,
            lowercaseHeaders,
            headers,
            fieldMap
          );
        }
      } else if (isMultiSubitem) {
        const subitemHeaders = lowercaseHeaders.filter((header) =>
          header.startsWith(`${lowerAPIName}_`)
        );

        if (subitemHeaders?.length > 0) {
          handleMultiSubitemAutoMap(
            newMappedHeaders,
            fieldAPIName,
            subitemHeaders,
            lowercaseHeaders,
            headers,
            fieldMap
          );
        }
      } else if (!fieldsToSkip.includes(fieldAPIName)) {
        if (isSingleRef) lowerAPIName = `${lowerAPIName}.id`;
        const foundIndex = lowercaseHeaders.indexOf(lowerAPIName);

        if (foundIndex > -1) {
          newMappedHeaders[fieldAPIName] = { Identifier: headers[foundIndex] };
        } else {
          const foundIndex2 = lowercaseHeaders.indexOf(lowerName);

          if (foundIndex2 > -1) {
            newMappedHeaders[fieldAPIName] = {
              Identifier: headers[foundIndex2],
            };
          }
        }
      }
    }

    setMappedHeaders(newMappedHeaders);
  }

  function autoRelate() {
    initializeMappedHeaders(
      fileHeaders?.length ? fileHeaders : Object.keys(mappedHeaders),
      true
    );
  }

  // Toggles ignore all for headers
  function ignoreAll() {
    const nextHeaders = produce(mappedHeaders, (newHeaders) => {
      for (const header of Object.keys(mappedHeaders)) {
        newHeaders[header] = { Identifier: "" };
      }
    });

    setMappedHeaders(nextHeaders);
  }

  // Handles when the field to match records against is changed.
  function handleMatchFieldChange(newFields) {
    handleChange({ matchFields: newFields });
    setSelectedMatchFields(newFields);
  }

  function handleHeadersChange(newHeaders) {
    setFileHeaders(newHeaders);
    initializeMappedHeaders(newHeaders);
  }

  function handleMatchActionChange(newAction) {
    setMatchAction(newAction);
    handleChange({ mAction: newAction });
  }
};

export default RecordCreateUpdateMenu;
